import { useState, type FormEvent } from 'react';
import type { UserSummaryDto } from '@/api/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import styles from './Users.module.css';

interface ResetPasswordDialogProps {
  user: UserSummaryDto;
  busy: boolean;
  onClose: () => void;
  onReset: (password: string) => Promise<void>;
}

export function ResetPasswordDialog({ user, busy, onClose, onReset }: ResetPasswordDialogProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('The temporary password must be at least 12 characters.');
      return;
    }
    try {
      await onReset(password);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to reset the password.');
    }
  }

  return (
    <Dialog
      title={`Reset password — ${user.displayName}`}
      onClose={onClose}
      actions={(
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="reset-password-form" disabled={busy}>
            {busy ? 'Resetting...' : 'Reset password'}
          </Button>
        </>
      )}
    >
      <form id="reset-password-form" className={styles.form} onSubmit={handleSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Input
          label="Temporary password"
          type="password"
          autoComplete="new-password"
          minLength={12}
          autoFocus
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <p className={styles.hint}>Existing sessions will expire and the user must change this password.</p>
      </form>
    </Dialog>
  );
}
